import { Box, Paper, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PushRateCrewData } from "../models/ShiftReportResponse";
import type { KPIData } from "@/shared/models/KpiData";
import {
	getZoneColor,
	GRAY_COLOR,
	TEXT,
	type SixZoneThresholds,
} from "@/pages/incentive/constants/colors";

type Props = {
	data: PushRateCrewData[];
	kpiData: KPIData | null;
	baseRoute?: string;
};

/** Format shift start date into "MM/DD" */
const formatDateLabel = (dateStr: string): string => {
	const d = new Date(dateStr);
	return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
};

/** Build PushRate thresholds (same as Push Rate gauge) from KPI data for use with getZoneColor */
const buildPushRateThresholds = (kpi: KPIData): SixZoneThresholds => ({
	Red: kpi.PushRateRed,
	Orange: kpi.PushRateOrange,
	Yellow: kpi.PushRateYellow,
	GreenSoft: kpi.PushRateGreenSoft,
	GreenNormal: kpi.PushRateGreenNormal,
	GreenHard: kpi.PushRateGreenHard,
});

export const PushRateCrewChart = ({
	data,
	kpiData,
	baseRoute = "/shift-report",
}: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);
	const navigate = useNavigate();

	const onChartClick = useCallback(
		(params: { dataIndex?: number }) => {
			const idx = params.dataIndex ?? -1;
			if (idx < 0 || idx >= data.length) return;
			const d = data[idx];
			if (d.ShiftId == null) return;
			navigate(`${baseRoute}/${d.ShiftId}`);
		},
		[data, navigate, baseRoute],
	);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(([entry]) => {
			setHeight(entry.contentRect.height);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const thresholds = kpiData ? buildPushRateThresholds(kpiData) : null;

	const chartOptions = useMemo<EChartsOption>(() => {
		if (data.length === 0) return {};

		const maxRate = Math.max(
			...data
				.filter((d) => d.PushRate != null)
				.map((d) => d.PushRate as number),
			1,
		);

		return {
			tooltip: {
				show: true,
				formatter: ((params: unknown) => {
					const p = params as { dataIndex?: number };
					const idx = p.dataIndex ?? 0;
					const d = data[idx];
					if (!d) return "No data";
					const pushRate = d.PushRate != null ? d.PushRate.toFixed(1) : "—";
					const pushRatePct =
						d.PushRatePct != null ? d.PushRatePct.toFixed(1) : "—";
					const lines = [
						`<strong>Crew ${d.Crew}</strong>${d.IsCurrentCrew ? " (Current)" : ""}`,
						`Shift: ${d.Shift}`,
						`Date: ${formatDateLabel(d.ShiftEnd)}`,
						`Push Rate: ${pushRate} billets/hr`,
						`Scheduled Attainment: ${pushRatePct}%`,
					];
					return lines.join("<br/>");
				}) as unknown as string,
				appendTo: document.body,
			},
			grid: {
				left: 2,
				right: 2,
				top: 20,
				bottom: 40,
				containLabel: false,
			},
			xAxis: {
				type: "category" as const,
				data: data.map((d) => {
					const label = `${d.Crew}\n${formatDateLabel(d.ShiftEnd)}`;
					return d.IsCurrentCrew ? `{bold|${label}}` : label;
				}),
				axisLabel: {
					interval: 0,
					fontSize: 10,
					color: TEXT.tertiary,
					lineHeight: 14,
					rich: {
						bold: {
							fontWeight: 700,
							color: "#212121",
							fontSize: 10,
							lineHeight: 14,
						},
					},
				},
				axisTick: { show: false },
				axisLine: { show: false },
			},
			yAxis: {
				type: "value" as const,
				show: false,
				min: 0,
				max: maxRate * 1.2,
			},
			series: [
				{
					type: "bar",
					cursor: "pointer",
					data: data.map((d) => {
						const hasData = d.PushRatePct != null && d.PushRatePct >= 0;
						const zoneColor =
							hasData && thresholds
								? getZoneColor(d.PushRatePct as number, thresholds)
								: GRAY_COLOR;

						return {
							value: hasData ? d.PushRate : maxRate * 0.1,
							itemStyle: {
								color: zoneColor.bg,
								borderRadius: [4, 4, 0, 0] as [number, number, number, number],
								opacity: 1,
								borderColor: "transparent",
								borderWidth: 0,
							},
						};
					}),
					barWidth: "65%",
					barMinHeight: 4,
					label: {
						show: true,
						position: "top" as const,
						formatter: (params: { dataIndex?: number }) => {
							const idx = params.dataIndex ?? 0;
							const d = data[idx];
							if (d.PushRate == null) return "";
							const tag = d.IsCurrentCrew ? "current" : "past";
							return `{${tag}|${d.PushRate.toFixed(1)}}`;
						},
						rich: {
							current: {
								fontSize: 11,
								fontWeight: 600,
								color: "#212121",
							},
							past: {
								fontSize: 11,
								fontWeight: 600,
								color: TEXT.tertiary,
							},
						},
					},
					silent: false,
				},
			],
		};
	}, [data, thresholds]);

	if (data.length === 0) {
		return (
			<Paper
				elevation={0}
				sx={{
					p: 1,
					height: "100%",
					width: "100%",
					display: "flex",
					flexDirection: "column",
					border: "1px solid #ddd",
					borderRadius: 1,
					backgroundColor: "#fff",
					userSelect: "none",
				}}
			>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: "0.85rem",
						lineHeight: 1,
						mb: 0.5,
					}}
				>
					Crew Avg. Push Rate
				</Typography>
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography variant="body2" color="text.secondary">
						No crew data
					</Typography>
				</Box>
			</Paper>
		);
	}

	return (
		<Paper
			elevation={0}
			sx={{
				p: 1,
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				border: "1px solid #ddd",
				borderRadius: 1,
				backgroundColor: "#fff",
				userSelect: "none",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					mb: 0.5,
				}}
			>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: "0.85rem",
						lineHeight: 1,
						whiteSpace: "nowrap",
					}}
				>
					Crew Avg. Push Rate
				</Typography>
			</Box>
			<Box
				ref={containerRef}
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
				}}
			>
				{height > 0 && (
					<ReactECharts
						option={chartOptions}
						notMerge
						style={{ height, width: "100%" }}
						opts={{ renderer: "svg" }}
						onEvents={{ click: onChartClick }}
					/>
				)}
			</Box>
		</Paper>
	);
};
